import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyAppointments } from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { CalendarDays, ChevronRight } from 'lucide-react';
import { getApiErrorMessage } from '../../utils/apiError';
import { toast } from 'sonner';

const statusLabels = {
  PENDIENTE: 'Pendiente',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  NO_SHOW: 'No asistió',
  COMPLETADA: 'Completada',
  REQUIERE_REAGENDAR: 'Por reagendar',
};

const statusColors = {
  PENDIENTE: 'bg-[#E09F3E]/10 text-[#E09F3E]',
  CONFIRMADA: 'bg-[#588157]/10 text-[#588157]',
  CANCELADA: 'bg-[#9E2A2B]/10 text-[#9E2A2B]',
  NO_SHOW: 'bg-[#8D99AE]/10 text-[#8D99AE]',
  COMPLETADA: 'bg-[#335C67]/10 text-[#335C67]',
  REQUIERE_REAGENDAR: 'bg-[#9E2A2B]/10 text-[#9E2A2B]',
};

export default function PatientAppointmentsList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMyAppointments({});
        const data = res.data?.data ?? res.data ?? [];
        setAppointments(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Error al cargar citas'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const sorted = [...appointments].sort((a, b) => {
    const d = (x) => x.date + (x.start_time || '');
    return d(a).localeCompare(d(b));
  }).reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-10 h-10 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2B2D42] font-['Manrope']">Mis citas</h1>
        <p className="text-[#5D5C61] mt-1">Historial y próximas citas.</p>
      </div>

      {sorted.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
            <p className="text-[#5D5C61]">No tienes citas registradas</p>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {sorted.map((apt) => (
            <li key={apt.id}>
              <Link to={`/mis-citas/${apt.id}`}>
                <Card className="border-slate-100 hover:border-[#335C67]/30 transition-colors cursor-pointer">
                  <CardContent className="py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="text-center shrink-0">
                        <p className="text-xl font-bold text-[#335C67]">
                          {new Date(apt.date + 'T00:00:00').getDate()}
                        </p>
                        <p className="text-xs text-[#8D99AE] uppercase">
                          {new Date(apt.date + 'T00:00:00').toLocaleDateString('es-MX', { month: 'short' })}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[#2B2D42]">{formatDate(apt.date)}</p>
                        <p className="text-sm text-[#5D5C61]">
                          {apt.start_time} - {apt.end_time}
                        </p>
                        {apt.reason && (
                          <p className="text-xs text-[#8D99AE] truncate">{apt.reason}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={statusColors[apt.status] || 'bg-slate-100 text-slate-600'}>
                        {statusLabels[apt.status] || apt.status}
                      </Badge>
                      <ChevronRight className="w-5 h-5 text-[#8D99AE]" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
